import React, { useState, useCallback, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { useQueryClient } from '@tanstack/react-query';

import Chat from './components/dealAgentChat';
import Header from './components/dealAgentHeader';
import ActionBar from './components/dealAgentActionBar';

import { useDealChatsFetch, useCurrentDealChatStream } from 'src/query/dealInsights/dealAgent';
import styles from './styles.module.scss';
import { findLast } from 'lodash';
import cx from 'classnames';

export interface DealAgentProps {
    /** When set, requests and summaries are scoped to this deal. */
    dealId?: string;
}

const Placeholder = () => {
    return (<div className={styles.placeholderContainer}>
        <div className={cx(styles.placeholder, styles.lg)}>
            <span className={cx(styles.line, styles.block)} />
        </div>
        <div className={cx(styles.placeholder, styles.md)}>
            <span className={cx(styles.line, styles.block)} />
        </div>
        <div className={cx(styles.placeholder, styles.sm)}>
            <span className={cx(styles.line, styles.block)} />
        </div>
    </div>)
};

const DealAgent: React.FC<DealAgentProps> = ({ dealId }) => {
    const [query, setQuery] = useState('');
    const [isHistoryView, setIsHistoryView] = useState(false);
    const { data: chats = [] } = useDealChatsFetch(dealId); // receive the same key to receive the chats
    const streamChatMutation = useCurrentDealChatStream(dealId); // use the mutation to ask the question

    // Methods
    const handleSubmit = () => {
        const question = query.trim();
        if (!question || streamChatMutation.isPending) return;

        const chatId = nanoid(6); // generate a unique chat id

        // Find last completed chat that has a session
        const lastAnswered: any = findLast([...chats], (c: any) => c.session && !c.creating);
        const session = lastAnswered?.session ?? '';

        streamChatMutation.mutate({
            chatId,
            question,
            session: session,
        });

        setQuery(''); // reset the query
    };

    return (
        <div className={styles.wrapper}>
            <Header
                isHistory={isHistoryView}
                onExitHistory={() => setIsHistoryView(false)}
                onOpenHistory={() => setIsHistoryView(true)}
            />

            {!dealId ? <Placeholder /> : <>
                <div className={styles.chatPanel}>
                    {isHistoryView ?
                        <div>History</div> :
                        <Chat
                            chats={chats}
                        />}
                </div>

                {!isHistoryView && (
                    <ActionBar
                        query={query}
                        onQueryChange={setQuery}
                        onSubmit={handleSubmit}
                        disabled={streamChatMutation.isPending}
                    />
                )}
            </>}

            <div className={styles.bottomOverlay} /> {/* bottom overlay */}
        </div>
    );
};

export default DealAgent;
